import React from 'react';
import { StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';

import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interMedium, interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';

type RelevanceBadgeValue = number | 'must-read' | 'related' | null | undefined;
type RelevanceBadgeVariant = 'must-read' | 'related';
type RelevanceBadgeSize = 'default' | 'compact';

interface RelevanceBadgeProps {
    relevanceClass: RelevanceBadgeValue;
    size?: RelevanceBadgeSize;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
}

function normalizeVariant(value: RelevanceBadgeValue): RelevanceBadgeVariant | null {
    if (value === 2 || value === 'must-read') {
        return 'must-read';
    }

    if (value === 1 || value === 'related') {
        return 'related';
    }

    return null;
}

export default function RelevanceBadge({
    relevanceClass,
    size = 'default',
    style,
    textStyle,
}: RelevanceBadgeProps) {
    const { fontsLoaded } = useFontLoading();
    const variant = normalizeVariant(relevanceClass);

    if (!variant) {
        return null;
    }

    const isMustRead = variant === 'must-read';
    const iconSize = size === 'compact' ? 10 : 12;

    return (
        <View
            style={[
                styles.base,
                size === 'compact' ? styles.compact : styles.default,
                isMustRead ? styles.mustRead : styles.related,
                style,
            ]}
        >
            <FontAwesome6
                name={isMustRead ? 'star' : 'link'}
                size={iconSize}
                color={isMustRead ? colors.text900 : colors.text700}
                solid={isMustRead}
            />
            <Text
                style={[
                    styles.textBase,
                    size === 'compact' ? styles.textCompact : styles.textDefault,
                    isMustRead ? styles.textMustRead : styles.textRelated,
                    fontsLoaded && (isMustRead ? interSemiBold : interMedium),
                    textStyle,
                ]}
            >
                {isMustRead ? <Trans id="papers.relevancebadge.mustRead" comment="Conditional UI copy in components/papers/RelevanceBadge.tsx">Must Read</Trans> : <Trans id="papers.relevancebadge.related" comment="Conditional UI copy in components/papers/RelevanceBadge.tsx">Related</Trans>}
            </Text>
        </View>
    );
}

const styles = StyleSheet.create({
    base: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 6,
        borderWidth: 1,
    },
    default: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        gap: 6,
    },
    compact: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        gap: 4,
    },
    mustRead: {
        backgroundColor: '#f9fafb',
        borderColor: '#d1d5db',
    },
    related: {
        backgroundColor: colors.bgWeak,
        borderColor: colors.border,
    },
    textBase: {
        color: colors.text700,
    },
    textDefault: {
        fontSize: 12,
    },
    textCompact: {
        fontSize: 11,
    },
    textMustRead: {
        color: colors.text900,
    },
    textRelated: {
        color: colors.text700,
    },
});
